import { useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import {
  NOTATION_DRILLS,
  NOTATION_MASTERY,
  describeSquare,
  explainSan,
  isPromotion,
  pickQuizMove,
  randomSquare,
  sanDistractors,
  shuffled,
  sideToMove,
  startPuzzle,
  targetsOf,
  type CardProgress,
  type NotationDrill,
  type QuizMove,
} from '@familyhub/shared';
import { Board } from './Board';
import { saveCard, type SchaakState } from './actions';
import type { Pack } from './pack';

/**
 * Learning to read chess notation (docs/SCHAAK.md §3.6): a lesson, then four
 * drills on real positions from the puzzle pack — find a square, name a square,
 * play a written move, and read a played one. Each drill keeps a `drill` card
 * (`ref` `notation:<drill>`): the counters, and in `box` the best streak (capped
 * at NOTATION_MASTERY, which is "beheerst"). No due date — drills are not part
 * of the review schedule.
 */

export const drillRef = (key: NotationDrill) => `notation:${key}`;

export function notationMastered(state: SchaakState): number {
  return NOTATION_DRILLS.filter((d) => (state.cards[drillRef(d.key)]?.box ?? 0) >= NOTATION_MASTERY).length;
}

export function NotationHome({
  state,
  onLesson,
  onDrill,
}: {
  state: SchaakState;
  onLesson: () => void;
  onDrill: (key: NotationDrill) => void;
}) {
  return (
    <main className="sk-main">
      <section className="sk-card">
        <h2>Notatie</h2>
        <p className="sk-dim">
          Zo schrijf je een zet op: <code>e4</code>, <code>Nxf7+</code>, <code>O-O</code>. Lees eerst de uitleg, oefen
          dan tot je {NOTATION_MASTERY} keer op rij goed zit.
        </p>
        <button className="btn" onClick={onLesson}>
          Uitleg
        </button>
      </section>
      <section className="sk-card">
        <ul className="sk-themes">
          {NOTATION_DRILLS.map((d) => {
            const c = state.cards[drillRef(d.key)];
            const best = c?.box ?? 0;
            return (
              <li key={d.key}>
                <button className="sk-theme" onClick={() => onDrill(d.key)}>
                  <span className="sk-theme-label">{d.label}</span>
                  <span className="sk-theme-state">
                    {best >= NOTATION_MASTERY ? '✓ beheerst' : c ? `beste reeks ${best}` : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </main>
  );
}

export function NotationLesson({ onStart }: { onStart: () => void }) {
  return (
    <main className="sk-main">
      <section className="sk-card sk-lesson">
        <h2>Schaaknotatie lezen</h2>

        <h3>1. Elk veld heeft een naam</h3>
        <p>
          De lijnen van links naar rechts (gezien vanaf wit) heten <code>a</code> tot <code>h</code>, de rijen van
          onder naar boven <code>1</code> tot <code>8</code>. Eerst de letter, dan het cijfer: <code>e4</code> is de
          e-lijn, vierde rij. Wit begint op rij 1 en 2, zwart op 7 en 8. Speel je zwart, dan staat het bord
          omgedraaid: <code>a1</code> zit dan rechtsboven.
        </p>
        <p>
          Ezelsbruggetje: <strong>a1 is een donker veld</strong>, en de dames beginnen op de d-lijn, op hun eigen
          kleur (de witte dame op <code>d1</code>, de zwarte op <code>d8</code>).
        </p>

        <h3>2. Een letter voor het stuk</h3>
        <table className="sk-table">
          <tbody>
            <tr>
              <td>
                <code>K</code>
              </td>
              <td>koning (King)</td>
            </tr>
            <tr>
              <td>
                <code>Q</code>
              </td>
              <td>dame (Queen)</td>
            </tr>
            <tr>
              <td>
                <code>R</code>
              </td>
              <td>toren (Rook)</td>
            </tr>
            <tr>
              <td>
                <code>B</code>
              </td>
              <td>loper (Bishop)</td>
            </tr>
            <tr>
              <td>
                <code>N</code>
              </td>
              <td>paard (kNight — de K was al bezet)</td>
            </tr>
            <tr>
              <td>—</td>
              <td>pion: geen letter, alleen het veld</td>
            </tr>
          </tbody>
        </table>
        <p>
          Dit zijn de Engelse letters, die Lichess, Chess.com en deze app gebruiken. In een Nederlands boek zie je K
          D T L P (koning, dame, toren, loper, paard).
        </p>

        <h3>3. Een zet = stuk + veld waar het heen gaat</h3>
        <ul>
          <li>
            <code>Nf3</code> — paard naar f3
          </li>
          <li>
            <code>e4</code> — pion naar e4
          </li>
          <li>
            <code>Bxc6</code> — loper <em>slaat</em> op c6 (<code>x</code> = slaan)
          </li>
          <li>
            <code>exd5</code> — de pion van de e-lijn slaat op d5 (bij een pion staat de lijn ervoor)
          </li>
        </ul>

        <h3>4. Tekens erachter</h3>
        <ul>
          <li>
            <code>+</code> schaak, <code>#</code> mat: <code>Qxf7#</code>
          </li>
          <li>
            <code>O-O</code> korte rokade (koningskant), <code>O-O-O</code> lange rokade (damekant)
          </li>
          <li>
            <code>e8=Q</code> pion promoveert tot dame
          </li>
          <li>
            <code>!</code> goede zet, <code>?</code> fout, <code>??</code> blunder, <code>!?</code> interessant — dat
            is commentaar, geen deel van de zet
          </li>
        </ul>

        <h3>5. Twee stukken kunnen naar hetzelfde veld</h3>
        <p>
          Dan zegt de notatie welke: <code>Nbd7</code> is het paard van de b-lijn, <code>R1e2</code> de toren van rij
          1. Alleen als het nodig is — anders staat er gewoon <code>Nd7</code>.
        </p>

        <h3>6. Zetnummers</h3>
        <p>
          <code>1. e4 e5 2. Nf3 Nc6</code>: elk nummer is een zet van wit én een van zwart. Begint een regel met een
          zet van zwart, dan staan er puntjes: <code>3... Nf6</code>.
        </p>

        <button className="btn btn-primary" onClick={onStart}>
          Oefen: Vind het veld
        </button>
      </section>
    </main>
  );
}

/* ---------------- the drills ---------------- */

type Question =
  | { kind: 'find'; fen: string; orientation: 'w' | 'b'; square: string }
  | { kind: 'name'; fen: string; orientation: 'w' | 'b'; square: string; options: string[] }
  | { kind: 'play'; fen: string; orientation: 'w' | 'b'; move: QuizMove }
  | { kind: 'read'; fen: string; orientation: 'w' | 'b'; move: QuizMove; options: string[] };

function makeQuestion(kind: NotationDrill, pack: Pack): Question {
  const rand = Math.random;
  for (;;) {
    const p = pack.puzzles[Math.floor(rand() * pack.puzzles.length)]!;
    const fen = startPuzzle(p).fen;
    // Mostly from white's side, often enough from black's that both are learned.
    const orientation = kind === 'play' || kind === 'read' ? sideToMove(fen) : rand() < 0.65 ? 'w' : 'b';
    if (kind === 'find') return { kind, fen, orientation, square: randomSquare(rand) };
    if (kind === 'name') {
      const square = randomSquare(rand);
      const near = new Set<string>();
      while (near.size < 3) {
        // Neighbouring squares and the mirror image — the mistakes people make.
        const f = square.charCodeAt(0) - 97;
        const r = Number(square[1]);
        const cands = [
          `${String.fromCharCode(97 + Math.max(0, Math.min(7, f + (rand() < 0.5 ? -1 : 1))))}${r}`,
          `${square[0]}${Math.max(1, Math.min(8, r + (rand() < 0.5 ? -1 : 1)))}`,
          `${String.fromCharCode(97 + (7 - f))}${9 - r}`,
          randomSquare(rand),
        ];
        const c = cands[Math.floor(rand() * cands.length)]!;
        if (c !== square) near.add(c);
      }
      return { kind, fen, orientation, square, options: shuffled([square, ...near], rand) };
    }
    const move = pickQuizMove(fen, rand);
    if (!move) continue;
    if (kind === 'play') return { kind, fen, orientation, move };
    return { kind, fen, orientation, move, options: shuffled([move.san, ...sanDistractors(fen, move, rand)], rand) };
  }
}

type Verdict = { right: boolean; text: string } | null;

export function NotationDrillScreen({
  drill,
  state,
  pack,
  userId,
}: {
  drill: NotationDrill;
  state: SchaakState;
  pack: Pack;
  userId: string;
}) {
  const info = NOTATION_DRILLS.find((d) => d.key === drill)!;
  // This drill's card, carried in memory for the session: answers come every
  // couple of seconds, faster than a Dexie read is reliably fresh.
  const card = useRef<CardProgress>(
    state.cards[drillRef(drill)] ?? {
      kind: 'drill',
      ref: drillRef(drill),
      theme: null,
      box: 0,
      due_at: null,
      attempts: 0,
      solved: 0,
      failed: 0,
      first_result: null,
      last_at: null,
    },
  );
  const [q, setQ] = useState<Question>(() => makeQuestion(drill, pack));
  const [streak, setStreak] = useState(0);
  const [verdict, setVerdict] = useState<Verdict>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  // For "read": the board shows the position after the move, a beat after the question appears.
  const [shown, setShown] = useState<{ fen: string; last: [string, string] | null }>({ fen: q.fen, last: null });

  useEffect(() => {
    setShown({ fen: q.fen, last: null });
    if (q.kind !== 'read') return;
    const t = setTimeout(() => {
      const c = new Chess(q.fen);
      c.move({ from: q.move.from, to: q.move.to, promotion: q.move.lan[4] });
      setShown({ fen: c.fen(), last: [q.move.from, q.move.to] });
    }, 500);
    return () => clearTimeout(t);
  }, [q]);

  const answer = (right: boolean, text: string) => {
    setVerdict({ right, text });
    const next = right ? streak + 1 : 0;
    setStreak(next);
    const c = card.current;
    card.current = {
      ...c,
      box: Math.min(NOTATION_MASTERY, Math.max(c.box, next)),
      attempts: c.attempts + 1,
      solved: c.solved + (right ? 1 : 0),
      failed: c.failed + (right ? 0 : 1),
      first_result: c.first_result ?? (right ? 'solved' : 'failed'),
      last_at: new Date().toISOString(),
    };
    void saveCard(userId, card.current);
  };

  const nextQuestion = () => {
    setVerdict(null);
    setSelected(null);
    setPicked(null);
    setQ(makeQuestion(drill, pack));
  };

  // A right answer moves on by itself; a wrong one waits to be read.
  useEffect(() => {
    if (!verdict?.right) return;
    const t = setTimeout(nextQuestion, 700);
    return () => clearTimeout(t);
  });

  const onSquare = (sq: string) => {
    if (verdict) return;
    if (q.kind === 'find') {
      setPicked(sq);
      return answer(sq === q.square, sq === q.square ? 'Goed!' : `Dat is ${sq}. Het groene veld is ${describeSquare(q.square)}.`);
    }
    if (q.kind !== 'play') return;
    const chess = new Chess(q.fen);
    if (selected && targetsOf(q.fen, selected).includes(sq)) {
      const right = selected === q.move.from && sq === q.move.to;
      let mine = `${selected}${sq}`;
      try {
        const promo = isPromotion(q.fen, selected, sq) ? (q.move.lan[4] ?? 'q') : undefined;
        mine = chess.move({ from: selected, to: sq, promotion: promo }).san;
      } catch {
        /* the san stays the squares */
      }
      setSelected(null);
      setPicked(right ? null : q.move.to);
      return answer(right, right ? `Goed! ${explainSan(q.move.san)}.` : `Jij speelde ${mine}. ${q.move.san} is: ${explainSan(q.move.san)}.`);
    }
    const piece = chess.get(sq as never);
    setSelected(piece && piece.color === sideToMove(q.fen) ? sq : null);
  };

  const choose = (opt: string) => {
    if (verdict) return;
    setPicked(opt);
    if (q.kind === 'name') answer(opt === q.square, opt === q.square ? 'Goed!' : describeSquare(q.square) + '.');
    if (q.kind === 'read') answer(opt === q.move.san, opt === q.move.san ? `Goed! ${explainSan(q.move.san)}.` : `${q.move.san}: ${explainSan(q.move.san)}.`);
  };

  const rightOption = q.kind === 'name' ? q.square : q.kind === 'read' ? q.move.san : null;
  const options = q.kind === 'name' || q.kind === 'read' ? q.options : undefined;

  const highlight = useMemo(() => {
    if (q.kind === 'name') return q.square;
    if (q.kind === 'find' && verdict && !verdict.right) return q.square;
    if (q.kind === 'play' && verdict && !verdict.right) return q.move.to;
    return null;
  }, [q, verdict]);

  return (
    <main className="sk-main">
      <p className="sk-tally" aria-live="polite">
        {info.label} · reeks {streak}
        {streak >= NOTATION_MASTERY ? ' ✓' : ` / ${NOTATION_MASTERY}`}
      </p>
      <p className="sk-dim" style={{ textAlign: 'center' }}>
        {info.intro}
      </p>
      {(q.kind === 'find' || q.kind === 'play') && (
        <p className="sk-san" aria-label={q.kind === 'find' ? `Veld ${q.square}` : `Zet ${q.move.san}`}>
          {q.kind === 'find' ? q.square : q.move.san}
        </p>
      )}
      <Board
        fen={shown.fen}
        orientation={q.orientation}
        selected={selected}
        targets={selected ? targetsOf(q.fen, selected) : []}
        lastMove={q.kind === 'play' && verdict && !verdict.right ? [q.move.from, q.move.to] : shown.last}
        check={null}
        highlight={highlight}
        coords={q.kind !== 'find' && q.kind !== 'name'}
        disabled={!!verdict || q.kind === 'name' || q.kind === 'read'}
        onSquare={onSquare}
      />
      {options && (
        <div className="sk-options" role="group" aria-label="Antwoorden">
          {options.map((o) => (
            <button
              key={o}
              className="sk-option"
              data-state={verdict ? (o === rightOption ? 'right' : o === picked ? 'wrong' : undefined) : undefined}
              disabled={!!verdict}
              onClick={() => choose(o)}
            >
              {o}
            </button>
          ))}
        </div>
      )}
      {verdict && (
        <p className={verdict.right ? 'sk-prompt sk-good' : 'sk-prompt sk-bad'} role="status">
          {verdict.text}
        </p>
      )}
      {verdict && !verdict.right && (
        <button className="btn btn-primary" onClick={nextQuestion} autoFocus>
          Volgende
        </button>
      )}
    </main>
  );
}
